import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { geoNaturalEarth1, geoPath, geoGraticule10, geoContains, geoCentroid } from "d3-geo";
import { zoom, zoomIdentity } from "d3-zoom";
import { select } from "d3-selection";
import "d3-transition";
import { mainPart, slug, splitTerritories } from "@/lib/geo";
import { Countries, CountryLabels, CityLayer, FriendPins, MePin } from "./MapLayers";
import { MapControls, MapLegend } from "./MapControls";

const W = 960;
const H = 500;

export const WorldMap = ({ geo, visited, wishlist, selected, onSelect, onCity, focus, compact, pins = [], me, onLocate }) => {
  const svgRef = useRef(null);
  const gRef = useRef(null);
  const zoomRef = useRef(null);
  const [k, setK] = useState(1);
  const [showCities, setShowCities] = useState(true);
  const [lambda, setLambda] = useState(0);
  const lambdaRef = useRef(0);
  const shownRef = useRef(0);
  const innerRef = useRef(null);
  const faceId = `world-face-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  useLayoutEffect(() => { shownRef.current = lambda; innerRef.current?.removeAttribute("transform"); }, [lambda]);

  const projection = useMemo(() => geoNaturalEarth1().rotate([lambda, 0]).fitExtent([[8, 8], [W - 8, H - 8]], { type: "Sphere" }), [lambda]);
  const path = useMemo(() => geoPath(projection), [projection]);
  const parts = useMemo(() => geo.features.map((f) => ({ f, main: mainPart(f), ...splitTerritories(f) })), [geo]);
  const shapes = useMemo(
    () =>
      parts.map(({ f, main, core, overseas }) => {
        // Countries cut by the map edge after spinning: label at the projected true centre, focus on a box around it
        const c = projection(geoCentroid(main)) || path.centroid(main);
        const b = path.bounds(main);
        const split = b[1][0] - b[0][0] > W * 0.6;
        return {
          name: f.properties.name, slug: slug(f.properties.name), d: path(core), dOverseas: overseas ? path(overseas) : null, overseas,
          c, area: path.area(main), bounds: split ? [[c[0] - 40, c[1] - 25], [c[0] + 40, c[1] + 25]] : b,
        };
      }),
    [parts, path, projection]
  );
  const rawCities = useMemo(() => {
    const known = new Set(geo.cities.map((c) => `${c.n}|${c.c}`));
    const extra = [...visited, ...wishlist].flatMap((v) => (v.places || [])
      .filter((p) => !known.has(`${p.name}|${v.country}`) && known.add(`${p.name}|${v.country}`))
      .map((p) => ({ n: p.name, c: v.country, x: p.x, y: p.y, r: 10, p: 0 })));
    return extra.length ? [...geo.cities, ...extra] : geo.cities;
  }, [visited, wishlist, geo]);
  const cities = useMemo(() => rawCities.map((c) => { const [px, py] = projection([c.x, c.y]); return { ...c, px, py }; }), [rawCities, projection]);

  const placedPins = useMemo(() => pins.map((p) => { const [px, py] = projection([p.x, p.y]); return { ...p, px, py }; }), [pins, projection]);
  const placedMe = useMemo(() => { if (!me) return null; const [px, py] = projection([me.x, me.y]); return { ...me, px, py }; }, [me, projection]);
  const fills = useMemo(() => {
    const m = {};
    wishlist.forEach((w) => (m[w.country] = "wish"));
    visited.forEach((v) => (m[v.country] = "visited"));
    return m;
  }, [visited, wishlist]);
  const visitedCities = useMemo(() => new Set(visited.flatMap((v) => v.cities.map((c) => `${c}|${v.country}`))), [visited]);
  const wishCities = useMemo(() => new Set(wishlist.flatMap((w) => (w.cities || []).map((c) => `${c}|${w.country}`))), [wishlist]);
  const visitedCountries = useMemo(() => new Set(visited.map((v) => v.country)), [visited]);
  // A territory (e.g. French Guiana) lights up only if you've been to a city inside it
  const litTerritories = useMemo(() => {
    const lit = new Set();
    parts.filter((s) => s.overseas).forEach((s) => {
      const name = s.f.properties.name;
      if (rawCities.some((c) => c.c === name && visitedCities.has(`${c.n}|${c.c}`) && geoContains(s.overseas, [c.x, c.y]))) lit.add(name);
    });
    return lit;
  }, [parts, rawCities, visitedCities]);

  useEffect(() => {
    // Move the map directly; only re-render labels/cities when zoom crosses a step, at most once per frame
    let frame = 0;
    const z = zoom().scaleExtent([1, 80]).translateExtent([[0, 0], [W, H]]).on("zoom", (e) => {
      select(gRef.current).attr("transform", e.transform);
      const stepped = Math.pow(2, Math.round(Math.log2(e.transform.k) * 4) / 4);
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setK(stepped));
    });
    zoomRef.current = z;
    select(svgRef.current).call(z).on("dblclick.zoom", null);
  }, []);

  useEffect(() => {
    // Spin the world only with a two-finger twist; one finger just moves the map
    const svg = svgRef.current;
    let frame = 0;
    let twist = null;
    const clones = () => svg.querySelectorAll(".world-clone");
    // While twisting, slide the drawn world (with a copy on each side so the edges never go blank); redraw once on release
    const show = () => {
      frame = 0;
      const d = ((lambdaRef.current - shownRef.current + 540) % 360) - 180;
      innerRef.current?.setAttribute("transform", `translate(${(d * (W - 16)) / 360},0)`);
    };
    const angle = (t) => (Math.atan2(t[1].clientY - t[0].clientY, t[1].clientX - t[0].clientX) * 180) / Math.PI;
    const tStart = (e) => {
      if (e.touches.length !== 2) return;
      twist = angle(e.touches);
      clones().forEach((c) => c.removeAttribute("display"));
    };
    const tMove = (e) => {
      if (e.touches.length !== 2 || twist === null) return;
      const a = angle(e.touches);
      const d = ((a - twist + 540) % 360) - 180;
      twist = a;
      lambdaRef.current = ((lambdaRef.current + d * 1.5 + 540) % 360) - 180;
      if (!frame) frame = requestAnimationFrame(show);
    };
    const tEnd = (e) => {
      if (twist === null || e.touches.length === 2) return;
      twist = null;
      cancelAnimationFrame(frame);
      frame = 0;
      show();
      clones().forEach((c) => c.setAttribute("display", "none"));
      const next = Math.round(lambdaRef.current * 4) / 4;
      if (next === shownRef.current) innerRef.current?.removeAttribute("transform");
      setLambda(next);
    };
    svg.addEventListener("touchstart", tStart, { passive: true, capture: true });
    svg.addEventListener("touchmove", tMove, { passive: true, capture: true });
    svg.addEventListener("touchend", tEnd, { passive: true, capture: true });
    svg.addEventListener("touchcancel", tEnd, { passive: true, capture: true });
    return () => {
      cancelAnimationFrame(frame);
      svg.removeEventListener("touchstart", tStart, true);
      svg.removeEventListener("touchmove", tMove, true);
      svg.removeEventListener("touchend", tEnd, true);
      svg.removeEventListener("touchcancel", tEnd, true);
    };
  }, []);

  const animate = (t) => select(svgRef.current).transition().duration(750).call(zoomRef.current.transform, t);
  const zoomBy = (f) => select(svgRef.current).transition().duration(300).call(zoomRef.current.scaleBy, f);

  useEffect(() => {
    if (!focus) return;
    const plain = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const pts = (focus.cities || []).map((n) => cities.find((c) => c.c === focus.name && plain(c.n) === plain(n)) || placedPins.find((p) => p.country === focus.name && plain(p.city) === plain(n))).filter(Boolean);
    const s = shapes.find((x) => x.name === focus.name);
    if (!pts.length && !s) return;
    // Zoom to the picked cities when we know where they are, otherwise to the whole country
    const pad = 6;
    const [[x0, y0], [x1, y1]] = pts.length
      ? [[Math.min(...pts.map((c) => c.px)) - pad, Math.min(...pts.map((c) => c.py)) - pad], [Math.max(...pts.map((c) => c.px)) + pad, Math.max(...pts.map((c) => c.py)) + pad]]
      : s.bounds;
    const scale = Math.min(pts.length ? 40 : 24, 0.7 / Math.max((x1 - x0) / W, (y1 - y0) / H));
    animate(zoomIdentity.translate(W / 2, H / 2).scale(scale).translate(-(x0 + x1) / 2, -(y0 + y1) / 2));
    // Only re-run for a new focus request, not while the world spins
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  return (
    <div className="relative w-full h-full" data-testid="world-map">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-full map-svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="neon-halo">
            <stop offset="0%" stopColor="#39FF88" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#39FF88" stopOpacity="0" />
          </radialGradient>
        </defs>
        <g ref={gRef}>
          <g ref={innerRef}>
          <g id={faceId}>
          <path d={path({ type: "Sphere" })} className="map-ocean" onClick={() => onSelect(null)} />
          <path d={path(geoGraticule10())} className="map-graticule" />
          <Countries shapes={shapes} fills={fills} lit={litTerritories} selected={selected} onSelect={onSelect} />
          <CountryLabels shapes={shapes} k={k} />
          {showCities && <CityLayer cities={cities} k={k} visitedCities={visitedCities} wishCities={wishCities} visitedCountries={visitedCountries} onCity={onCity} />}
          {placedPins.length > 0 && <FriendPins pins={placedPins} k={k} />}
          {placedMe && <MePin me={placedMe} k={k} />}
          </g>
          <use href={`#${faceId}`} x={-(W - 16)} className="world-clone pointer-events-none" display="none" />
          <use href={`#${faceId}`} x={W - 16} className="world-clone pointer-events-none" display="none" />
          </g>
        </g>
      </svg>
      {!compact && <MapLegend />}
      <MapControls
        onIn={() => zoomBy(1.8)}
        onOut={() => zoomBy(1 / 1.8)}
        onReset={() => { lambdaRef.current = 0; setLambda(0); animate(zoomIdentity); }}
        showCities={showCities}
        onToggleCities={() => setShowCities((v) => !v)}
        onLocate={onLocate}
        locating={!!me}
      />
    </div>
  );
};
